import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import * as Cesium from 'cesium';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { CameraService } from './camera.service';
import { WindowService } from './window.service';
import { ConfigurationManagerService } from './configuration-manager.service';
import { getAuthToken } from '../utility/login-info';

export interface CellTower {
  cell_id: string;
  site_id: string;
  region: string;
  latitude: number;
  longitude: number;
  lac: string;
  tac: string;
  sector: string;
  supported_rat: string;
  site_status: 'Operational' | 'Maintenance' | string;
}

export interface RegionCluster {
  name: string;
  color: string;
  towers: CellTower[];
  centerLat: number;
  centerLon: number;
  count: number;
}

export interface TowerCdrStats {
  totalEvents: number;
  voiceCount: number;
  smsCount: number;
  dataCount: number;
  totalDurationSec: number;
  totalMB: number;
  handoverCount: number;
  uniqueSubscribers: number;
}

export interface CdrRecord {
  cdr_id: string;
  event_type: string;
  event_start: string;
  event_end: string;
  duration_sec: number;
  subscriber_id: string;
  msisdn_synthetic: string;
  other_party_synthetic: string;
  imsi_synthetic: string;
  imei_synthetic: string;
  device_type: string;
  tariff_plan: string;
  direction: string;
  event_status: string;
  start_cell_id: string;
  end_cell_id: string;
  region: string;
  tower_latitude: number;
  tower_longitude: number;
  radio_access_technology: string;
  bytes_up: number;
  bytes_down: number;
  total_mb: number;
  handover_count: number;
}

export interface Subscriber {
  subscriber_id: string;
  msisdn_synthetic: string;
  imsi_synthetic: string;
  imei_synthetic: string;
  device_type: string;
  tariff_plan: string;
  home_region: string;
  customer_segment: string;
  subscriber_status: string;
}

export interface DataDictionaryItem {
  field: string;
  description: string;
}

export interface HoverTooltipInfo {
  x: number;
  y: number;
  tower: CellTower;
  stats?: TowerCdrStats;
}

// Region colors matching reference map
export const REGION_COLORS: Record<string, string> = {
  Abuja: '#EC4899',       // Pink
  Ibadan: '#06B6D4',      // Cyan
  Jos: '#3B82F6',         // Blue
  Kaduna: '#F97316',      // Orange
  Kano: '#EF4444',        // Red
  Lagos: '#2563EB',       // Darker Blue / Indigo
  Maiduguri: '#22C55E',   // Green
  'Port Harcourt': '#84CC16', // Lime / Chartreuse
  Sokoto: '#A855F7',      // Purple
  Yola: '#EAB308',        // Yellow
};

@Injectable({
  providedIn: 'root'
})
export class CdrGlobeService {
  private viewer?: Cesium.Viewer;
  private cdrDataSource?: Cesium.CustomDataSource;

  private regionsSubject = new BehaviorSubject<RegionCluster[]>([]);
  public regions$: Observable<RegionCluster[]> = this.regionsSubject.asObservable();

  private totalTowersSubject = new BehaviorSubject<number>(0);
  public totalTowers$: Observable<number> = this.totalTowersSubject.asObservable();

  private subscribersSubject = new BehaviorSubject<Subscriber[]>([]);
  public subscribers$: Observable<Subscriber[]> = this.subscribersSubject.asObservable();

  private dataDictionarySubject = new BehaviorSubject<DataDictionaryItem[]>([]);
  public dataDictionary$: Observable<DataDictionaryItem[]> = this.dataDictionarySubject.asObservable();

  private isLayerVisibleSubject = new BehaviorSubject<boolean>(false);
  public isLayerVisible$: Observable<boolean> = this.isLayerVisibleSubject.asObservable();

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

  public isLayerVisible(): boolean {
    return this.isLayerVisibleSubject.getValue();
  }

  private selectedTowerSubject = new BehaviorSubject<{
    tower: CellTower;
    stats: TowerCdrStats;
    recentCdrs: CdrRecord[];
  } | null>(null);
  public selectedTower$ = this.selectedTowerSubject.asObservable();

  private hoverTooltipSubject = new BehaviorSubject<HoverTooltipInfo | null>(null);
  public hoverTooltip$ = this.hoverTooltipSubject.asObservable();

  private towersMap = new Map<string, CellTower>();
  private towerStatsMap = new Map<string, TowerCdrStats>();
  private towerCdrsMap = new Map<string, CdrRecord[]>();
  private allCdrs: CdrRecord[] = [];

  private isInitialized = false;
  private pointerMoveHandler?: Cesium.ScreenSpaceEventHandler;
  private clickHandler?: Cesium.ScreenSpaceEventHandler;

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private cameraService: CameraService,
    private windowService: WindowService
  ) { }

  /**
   * Initialize CDR visualization with Cesium Viewer
   */
  async init(viewer: Cesium.Viewer): Promise<void> {
    this.viewer = viewer;

    this.cleanupMouseEvents();

    if (this.cdrDataSource) {
      try {
        if (this.viewer?.dataSources?.contains(this.cdrDataSource)) {
          this.viewer.dataSources.remove(this.cdrDataSource, true);
        }
      } catch (e) { }
    }

    this.cdrDataSource = new Cesium.CustomDataSource('CDR_TOWERS_DATA_SOURCE');
    await this.viewer.dataSources.add(this.cdrDataSource);

    this.setupMouseEvents();

    if (this.towersMap.size === 0) {
      await this.loadAllData();
    }

    this.renderAllGlobeEntities();
    this.isInitialized = true;
    this.cdrDataSource.show = this.isLayerVisibleSubject.getValue();
  }

  /**
   * Helper to make POST API request to CDR endpoint
   */
  private async postCdrApi<T>(url: string): Promise<T[]> {
    const token = getAuthToken();
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });
    if (token) {
      headers = headers.set('Authorization', token.startsWith('Bearer ') ? token : `Bearer ${token}`);
    }

    try {
      const response = await firstValueFrom(
        this.http.post<T[]>(url, {}, { headers })
      );
      return Array.isArray(response) ? response : [];
    } catch (err) {
      console.error(`[CdrGlobeService] Error calling API endpoint ${url}:`, err);
      return [];
    }
  }

  /**
   * Load CDR Data directly from APIs (BaseURL + CdrCellTowers, CdrRecords, CdrSubscribers, CdrDataDictionary)
   */
  private async loadAllData(): Promise<void> {
    this.isLoadingSubject.next(true);
    const settings = ConfigurationManagerService.settings;
    try {
      // 1. Fetch Cell Towers from API (BaseURL + CdrCellTowers)
      const cellTowersData = await this.postCdrApi<any>(settings.BaseURL + settings.CdrCellTowers);
      this.processCellTowers(cellTowersData);

      // 2. Fetch CDR Records from API (BaseURL + CdrRecords)
      const cdrRecordsData = await this.postCdrApi<any>(settings.BaseURL + settings.CdrRecords);
      this.processCdrRecords(cdrRecordsData);

      // 3. Fetch Subscribers from API (BaseURL + CdrSubscribers)
      const subscribersData = await this.postCdrApi<any>(settings.BaseURL + settings.CdrSubscribers);
      this.processSubscribers(subscribersData);

      // 4. Fetch Data Dictionary from API (BaseURL + CdrDataDictionary)
      const dictData = await this.postCdrApi<any>(settings.BaseURL + settings.CdrDataDictionary);
      this.processDataDictionary(dictData);
    } catch (error) {
      console.error('[CdrGlobeService] Error loading CDR data:', error);
    } finally {
      this.isLoadingSubject.next(false);
    }
  }

  /**
   * Process Cell Towers JSON from API
   */
  private processCellTowers(items: any[]): void {
    if (!Array.isArray(items) || items.length === 0) return;

    const towers: CellTower[] = [];
    const regionGroups: Record<string, CellTower[]> = {};
    this.towersMap.clear();

    for (const item of items) {
      const tower: CellTower = {
        cell_id: String(item.cell_id || '').trim(),
        site_id: String(item.site_id || '').trim(),
        region: String(item.region || '').trim(),
        latitude: typeof item.latitude === 'number' ? item.latitude : parseFloat(item.latitude),
        longitude: typeof item.longitude === 'number' ? item.longitude : parseFloat(item.longitude),
        lac: String(item.lac || '').trim(),
        tac: String(item.tac || '').trim(),
        sector: String(item.sector || 'Sector_A').trim(),
        supported_rat: String(item.supported_rat || '4G/5G').trim(),
        site_status: String(item.site_status || 'Operational').trim(),
      };

      if (!isNaN(tower.latitude) && !isNaN(tower.longitude) && tower.cell_id) {
        towers.push(tower);
        this.towersMap.set(tower.cell_id, tower);

        if (!regionGroups[tower.region]) {
          regionGroups[tower.region] = [];
        }
        regionGroups[tower.region].push(tower);
      }
    }

    const clusters: RegionCluster[] = Object.keys(regionGroups)
      .sort((a, b) => a.localeCompare(b))
      .map(regionName => {
        const list = regionGroups[regionName];
        const centerLat = list.reduce((sum, t) => sum + t.latitude, 0) / list.length;
        const centerLon = list.reduce((sum, t) => sum + t.longitude, 0) / list.length;
        return {
          name: regionName,
          color: REGION_COLORS[regionName] || '#00F0FF',
          towers: list,
          centerLat,
          centerLon,
          count: list.length
        };
      });

    this.regionsSubject.next(clusters);
    this.totalTowersSubject.next(towers.length);
  }

  /**
   * Process CDR Records JSON from API and aggregate per tower
   */
  private processCdrRecords(items: any[]): void {
    if (!Array.isArray(items) || items.length === 0) return;

    this.allCdrs = [];
    this.towerStatsMap.clear();
    this.towerCdrsMap.clear();
    const subscriberMap = new Map<string, Set<string>>();

    for (const item of items) {
      const record: CdrRecord = {
        cdr_id: String(item.cdr_id || '').trim(),
        event_type: String(item.event_type || '').trim(),
        event_start: String(item.event_start || '').trim(),
        event_end: String(item.event_end || '').trim(),
        duration_sec: typeof item.duration_sec === 'number' ? item.duration_sec : (parseFloat(item.duration_sec) || 0),
        subscriber_id: String(item.subscriber_id || '').trim(),
        msisdn_synthetic: String(item.msisdn_synthetic || '').trim(),
        other_party_synthetic: String(item.other_party_synthetic || '').trim(),
        imsi_synthetic: String(item.imsi_synthetic || '').trim(),
        imei_synthetic: String(item.imei_synthetic || '').trim(),
        device_type: String(item.device_type || '').trim(),
        tariff_plan: String(item.tariff_plan || '').trim(),
        direction: String(item.direction || '').trim(),
        event_status: String(item.event_status || '').trim(),
        start_cell_id: String(item.start_cell_id || '').trim(),
        end_cell_id: String(item.end_cell_id || '').trim(),
        region: String(item.region || '').trim(),
        tower_latitude: typeof item.tower_latitude === 'number' ? item.tower_latitude : parseFloat(item.tower_latitude),
        tower_longitude: typeof item.tower_longitude === 'number' ? item.tower_longitude : parseFloat(item.tower_longitude),
        radio_access_technology: String(item.radio_access_technology || '').trim(),
        bytes_up: typeof item.bytes_up === 'number' ? item.bytes_up : (parseFloat(item.bytes_up) || 0),
        bytes_down: typeof item.bytes_down === 'number' ? item.bytes_down : (parseFloat(item.bytes_down) || 0),
        total_mb: typeof item.total_mb === 'number' ? item.total_mb : (parseFloat(item.total_mb) || 0),
        handover_count: typeof item.handover_count === 'number' ? item.handover_count : (parseInt(item.handover_count, 10) || 0),
      };

      this.allCdrs.push(record);

      const cellId = record.start_cell_id;
      if (cellId) {
        if (!this.towerStatsMap.has(cellId)) {
          this.towerStatsMap.set(cellId, {
            totalEvents: 0,
            voiceCount: 0,
            smsCount: 0,
            dataCount: 0,
            totalDurationSec: 0,
            totalMB: 0,
            handoverCount: 0,
            uniqueSubscribers: 0,
          });
          subscriberMap.set(cellId, new Set());
          this.towerCdrsMap.set(cellId, []);
        }

        const stats = this.towerStatsMap.get(cellId)!;
        stats.totalEvents++;
        if (record.event_type.startsWith('VOICE')) {
          stats.voiceCount++;
          stats.totalDurationSec += record.duration_sec;
        } else if (record.event_type.startsWith('SMS')) {
          stats.smsCount++;
        } else if (record.event_type.startsWith('DATA')) {
          stats.dataCount++;
          stats.totalMB += record.total_mb;
        }
        stats.handoverCount += record.handover_count;
        if (record.subscriber_id) {
          subscriberMap.get(cellId)!.add(record.subscriber_id);
        }

        const cdrsList = this.towerCdrsMap.get(cellId)!;
        if (cdrsList.length < 50) {
          cdrsList.push(record);
        }
      }
    }

    // Set unique subscribers count
    subscriberMap.forEach((subs, cellId) => {
      const stats = this.towerStatsMap.get(cellId);
      if (stats) {
        stats.uniqueSubscribers = subs.size;
      }
    });
  }

  /**
   * Process Subscribers JSON from API
   */
  private processSubscribers(items: any[]): void {
    if (!Array.isArray(items)) return;
    const subscribers: Subscriber[] = items.map(s => ({
      subscriber_id: String(s.subscriber_id || '').trim(),
      msisdn_synthetic: String(s.msisdn_synthetic || '').trim(),
      imsi_synthetic: String(s.imsi_synthetic || '').trim(),
      imei_synthetic: String(s.imei_synthetic || '').trim(),
      device_type: String(s.device_type || '').trim(),
      tariff_plan: String(s.tariff_plan || '').trim(),
      home_region: String(s.home_region || '').trim(),
      customer_segment: String(s.customer_segment || '').trim(),
      subscriber_status: String(s.subscriber_status || '').trim(),
    }));
    this.subscribersSubject.next(subscribers);
  }

  /**
   * Process Data Dictionary JSON from API
   */
  private processDataDictionary(items: any[]): void {
    if (!Array.isArray(items)) return;
    const dict: DataDictionaryItem[] = items.map(d => ({
      field: String(d.field || '').trim(),
      description: String(d.description || '').trim(),
    }));
    this.dataDictionarySubject.next(dict);
  }

  /**
   * Render all entities on the Cesium Globe
   */
  private renderAllGlobeEntities(): void {
    if (!this.viewer || !this.cdrDataSource) return;
    this.cdrDataSource.entities.removeAll();

    // 1. Draw Geographic / Surrounding Labels
    this.renderGeographicLabels();

    // 2. Render City Clusters, Hubs, Spokes, Labels, and Tower Pins
    this.renderCityClusters();

    this.viewer.scene.requestRender();
  }

  /**
   * Render Reference Geographic Labels (NIGERIA, NIGER, CHAD, CAMEROON, Atlantic Ocean)
   */
  private renderGeographicLabels(): void {
    if (!this.cdrDataSource) return;
    // Country Main Label
    this.cdrDataSource.entities.add({
      name: 'Label NIGERIA',
      position: Cesium.Cartesian3.fromDegrees(8.6753, 8.2),
      label: {
        text: '',
        font: 'bold 28px "Inter", "Segoe UI", sans-serif',
        fillColor: Cesium.Color.WHITE.withAlpha(0.88),
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 4,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        verticalOrigin: Cesium.VerticalOrigin.CENTER,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(500000, 15000000),
      },
    });

    // Neighbor Country: NIGER (North)
    this.cdrDataSource.entities.add({
      name: 'Label NIGER',
      position: Cesium.Cartesian3.fromDegrees(8.5, 14.8),
      label: {
        text: '',
        font: 'bold 22px "Inter", sans-serif',
        fillColor: Cesium.Color.fromCssColorString('#94A3B8'),
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 3,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(800000, 15000000),
      },
    });

    // Neighbor Country: CHAD (Northeast)
    this.cdrDataSource.entities.add({
      name: 'Label CHAD',
      position: Cesium.Cartesian3.fromDegrees(15.2, 14.2),
      label: {
        text: '',
        font: 'bold 22px "Inter", sans-serif',
        fillColor: Cesium.Color.fromCssColorString('#94A3B8'),
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 3,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(800000, 15000000),
      },
    });

    // Neighbor Country: CAMEROON (East)
    this.cdrDataSource.entities.add({
      name: 'Label CAMEROON',
      position: Cesium.Cartesian3.fromDegrees(14.0, 6.2),
      label: {
        text: '',
        font: 'bold 22px "Inter", sans-serif',
        fillColor: Cesium.Color.fromCssColorString('#94A3B8'),
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 3,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(800000, 15000000),
      },
    });

    // Ocean Label: Atlantic Ocean (Southwest)
    this.cdrDataSource.entities.add({
      name: 'Label Atlantic Ocean',
      position: Cesium.Cartesian3.fromDegrees(3.2, 4.0),
      label: {
        text: '',
        font: 'italic 20px "Inter", sans-serif',
        fillColor: Cesium.Color.fromCssColorString('#38BDF8').withAlpha(0.85),
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 3,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(600000, 15000000),
      },
    });
  }

  /**
   * Render City Clusters: Hubs, Spokes, City Labels, and Tower Markers
   */
  private renderCityClusters(): void {
    if (!this.cdrDataSource) return;
    const clusters = this.regionsSubject.getValue();

    for (const cluster of clusters) {
      const cesiumColor = Cesium.Color.fromCssColorString(cluster.color);
      const hubPos = Cesium.Cartesian3.fromDegrees(cluster.centerLon, cluster.centerLat);

      // 1. City Hub Center Marker
      this.cdrDataSource.entities.add({
        name: `${cluster.name} Hub`,
        position: hubPos,
        point: {
          pixelSize: 12,
          color: Cesium.Color.BLACK,
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 2.5,
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
      });

      // 2. City Name Label
      this.cdrDataSource.entities.add({
        name: `City Label: ${cluster.name}`,
        position: hubPos,
        label: {
          text: ` ${cluster.name}`,
          font: 'bold 16px "Inter", "Segoe UI", sans-serif',
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 4,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          horizontalOrigin: Cesium.HorizontalOrigin.LEFT,
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          pixelOffset: new Cesium.Cartesian2(14, 0),
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 20000000),
          eyeOffset: new Cesium.Cartesian3(0, 0, -100),
        },
      });

      // 3. Render Individual Towers and Spoke Lines
      for (const tower of cluster.towers) {
        const towerPos = Cesium.Cartesian3.fromDegrees(tower.longitude, tower.latitude);

        // Radial spoke line from hub to tower
        this.cdrDataSource.entities.add({
          id: `CDR_SPOKE_${tower.cell_id}`,
          name: `Spoke: ${tower.cell_id}`,
          polyline: {
            positions: [hubPos, towerPos],
            width: 2.0,
            material: new Cesium.ColorMaterialProperty(
              cesiumColor.withAlpha(0.85)
            ),
            clampToGround: true,
          },
          properties: new Cesium.PropertyBag({
            isCdrTower: true,
            towerData: tower,
            regionColor: cluster.color,
          }),
        });

        // Generate circular canvas pin dataURL
        const pinDataUrl = this.createCirclePinDataUrl(cluster.color);

        // Tower Pin Entity
        this.cdrDataSource.entities.add({
          id: `CDR_TOWER_${tower.cell_id}`,
          name: `Cell Tower: ${tower.cell_id}`,
          position: towerPos,
          billboard: {
            image: pinDataUrl,
            width: 24,
            height: 24,
            verticalOrigin: Cesium.VerticalOrigin.CENTER,
            horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            eyeOffset: new Cesium.Cartesian3(0, 0, -50),
          },
          point: {
            pixelSize: 15,
            color: cesiumColor,
            outlineColor: Cesium.Color.fromCssColorString('#0B0F19'),
            outlineWidth: 3,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
          properties: new Cesium.PropertyBag({
            isCdrTower: true,
            towerData: tower,
            regionColor: cluster.color,
          }),
        });
      }
    }
  }

  /**
   * Helper to create circular canvas marker with dark border dataURL
   */
  private createCirclePinDataUrl(colorHex: string): string {
    const canvas = document.createElement('canvas');
    canvas.width = 48;
    canvas.height = 48;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    const centerX = 24;
    const centerY = 24;
    const radius = 18;

    // Drop shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
    ctx.shadowBlur = 6;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 2;

    // Outer dark ring
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2, false);
    ctx.fillStyle = '#090D16';
    ctx.fill();

    // Reset shadow
    ctx.shadowColor = 'transparent';

    // Inner colored circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius - 3, 0, Math.PI * 2, false);
    ctx.fillStyle = colorHex;
    ctx.fill();

    // Inner white highlight dot
    ctx.beginPath();
    ctx.arc(centerX - 4, centerY - 4, 3.5, 0, Math.PI * 2, false);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.fill();

    return canvas.toDataURL();
  }

  /**
   * Robust & lightweight helper to find picked Cell Tower entity from screen position
   */
  private findPickedTower(position: Cesium.Cartesian2): CellTower | null {
    if (!this.viewer || !position) return null;

    const extractTower = (picked: any): CellTower | null => {
      if (!Cesium.defined(picked) || !picked.id) return null;
      const entityId = picked.id;

      // Case 1: String ID
      if (typeof entityId === 'string') {
        if (entityId.startsWith('CDR_TOWER_') || entityId.startsWith('CDR_SPOKE_')) {
          const cellId = entityId.replace('CDR_TOWER_', '').replace('CDR_SPOKE_', '');
          const tower = this.towersMap.get(cellId);
          if (tower) return tower;
        }
      }

      // Case 2: Entity object with .id property
      if (typeof entityId.id === 'string') {
        if (entityId.id.startsWith('CDR_TOWER_') || entityId.id.startsWith('CDR_SPOKE_')) {
          const cellId = entityId.id.replace('CDR_TOWER_', '').replace('CDR_SPOKE_', '');
          const tower = this.towersMap.get(cellId);
          if (tower) return tower;
        }
      }

      // Case 3: PropertyBag properties
      if (entityId.properties) {
        if (typeof entityId.properties.isCdrTower?.getValue === 'function' && entityId.properties.isCdrTower.getValue()) {
          const t = entityId.properties.towerData?.getValue?.();
          if (t && t.cell_id) return t;
        }
        if (entityId.properties.towerData) {
          const t = typeof entityId.properties.towerData.getValue === 'function'
            ? entityId.properties.towerData.getValue()
            : entityId.properties.towerData;
          if (t && t.cell_id) return t;
        }
      }

      return null;
    };

    // 1. Single pick first (Fast)
    let singlePicked: any = null;
    try {
      singlePicked = this.viewer.scene.pick(position);
    } catch (e) {
      return null;
    }

    if (!singlePicked) {
      // Empty space / terrain, nothing picked
      return null;
    }

    const towerFromSingle = extractTower(singlePicked);
    if (towerFromSingle) return towerFromSingle;

    // 2. Only drill pick if something was picked that was NOT a tower (e.g., city hub or label occluding tower)
    try {
      const drillPicks = this.viewer.scene.drillPick(position, 4) || [];
      for (const p of drillPicks) {
        const towerFromDrill = extractTower(p);
        if (towerFromDrill) return towerFromDrill;
      }
    } catch (e) {
      // In case drillPick is not supported in current mode
    }

    return null;
  }

  /**
   * Setup hover & click screen space event handlers with RAF throttling and runOutsideAngular
   */
  private setupMouseEvents(): void {
    if (!this.viewer) return;

    this.ngZone.runOutsideAngular(() => {
      this.pointerMoveHandler = new Cesium.ScreenSpaceEventHandler(this.viewer!.scene.canvas);

      let pendingPosition: Cesium.Cartesian2 | null = null;
      let isRafScheduled = false;
      let lastHoveredCellId: string | null = null;

      this.pointerMoveHandler.setInputAction((movement: any) => {
        // If CDR layer is turned off or modal dialog is open, do not show or keep hover tooltip
        if (!this.isLayerVisibleSubject.getValue() || this.selectedTowerSubject.getValue() !== null) {
          if (lastHoveredCellId !== null) {
            lastHoveredCellId = null;
            this.ngZone.run(() => {
              this.hoverTooltipSubject.next(null);
            });
          }
          return;
        }

        pendingPosition = movement.endPosition;
        if (isRafScheduled) return;

        isRafScheduled = true;
        requestAnimationFrame(() => {
          isRafScheduled = false;
          if (!pendingPosition || !this.viewer) return;

          const pos = pendingPosition;
          const tower = this.findPickedTower(pos);

          if (tower) {
            lastHoveredCellId = tower.cell_id;
            const stats = this.towerStatsMap.get(tower.cell_id) || {
              totalEvents: 0,
              voiceCount: 0,
              smsCount: 0,
              dataCount: 0,
              totalDurationSec: 0,
              totalMB: 0,
              handoverCount: 0,
              uniqueSubscribers: 0,
            };
            this.ngZone.run(() => {
              this.hoverTooltipSubject.next({
                x: pos.x,
                y: pos.y,
                tower,
                stats,
              });
            });
          } else {
            if (lastHoveredCellId !== null) {
              lastHoveredCellId = null;
              this.ngZone.run(() => {
                this.hoverTooltipSubject.next(null);
              });
            }
          }
        });
      }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

      this.clickHandler = new Cesium.ScreenSpaceEventHandler(this.viewer!.scene.canvas);
      this.clickHandler.setInputAction((click: any) => {
        // If CDR layer is turned off, ignore clicks
        if (!this.isLayerVisibleSubject.getValue()) return;

        const tower = this.findPickedTower(click.position);
        if (tower) {
          lastHoveredCellId = null;
          // Immediately dismiss hover tooltip when opening full modal
          this.ngZone.run(() => {
            this.hoverTooltipSubject.next(null);
          });

          const stats = this.towerStatsMap.get(tower.cell_id) || {
            totalEvents: 0,
            voiceCount: 0,
            smsCount: 0,
            dataCount: 0,
            totalDurationSec: 0,
            totalMB: 0,
            handoverCount: 0,
            uniqueSubscribers: 0,
          };
          const recentCdrs = this.towerCdrsMap.get(tower.cell_id) || [];
          this.ngZone.run(() => {
            this.selectedTowerSubject.next({
              tower,
              stats,
              recentCdrs,
            });
          });
        }
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    });
  }

  private cleanupMouseEvents(): void {
    if (this.pointerMoveHandler && !this.pointerMoveHandler.isDestroyed()) {
      try { this.pointerMoveHandler.destroy(); } catch (e) { }
    }
    this.pointerMoveHandler = undefined;
    if (this.clickHandler && !this.clickHandler.isDestroyed()) {
      try { this.clickHandler.destroy(); } catch (e) { }
    }
    this.clickHandler = undefined;
  }

  /**
   * Toggle visibility of CDR layer
   */
  toggleLayer(visible?: boolean): void {
    const newState = visible !== undefined ? visible : !this.isLayerVisibleSubject.getValue();
    this.isLayerVisibleSubject.next(newState);
    if (this.cdrDataSource) {
      this.cdrDataSource.show = newState;
    }
    if (this.viewer) {
      this.viewer.scene.requestRender();
    }
  }

  /**
   * Close building camera component and restore all toolbar buttons
   */
  closeBuildingCamera(): void {
    try {
      this.cameraService.useEntityCamera(null);
      this.cameraService.setIsBuildingCameraOpen(false);
      this.cameraService.setHideEventDetailsForLiveNotification(false);
      this.windowService.closeAllMapEntityDetailsWindow();

      if (this.viewer) {
        const v = this.viewer as any;

        // Show Home Button
        if (v.homeButton && v.homeButton._element) {
          const el = v.homeButton._element;
          if (el && el.style) {
            el.style.display = '';
          }
        }

        // Show Geocoder
        if (v.geocoder && v.geocoder._element) {
          const el = v.geocoder._element;
          if (el && el.style) {
            el.style.display = '';
          }
        }

        // Show Geocoder Container
        const geocoderContainer = document.querySelector('.cesium-viewer-geocoderContainer');
        if (geocoderContainer && (geocoderContainer as HTMLElement).style) {
          (geocoderContainer as HTMLElement).style.display = '';
        }

        // Show BaseLayerPicker
        if (v.baseLayerPicker && v.baseLayerPicker._element) {
          const el = v.baseLayerPicker._element;
          if (el && el.style) {
            el.style.display = '';
          }
        }
      }
    } catch (e) {
      console.warn('Error closing building camera:', e);
    }
  }

  /**
   * Fly camera to full view of Nigeria
   */
  flyToNigeria(duration: number = 1.8): void {
    this.closeBuildingCamera();
    if (!this.viewer) return;
    // Nigeria bounding rectangle with comfortable margins: [west, south, east, north]
    const nigeriaRectangle = Cesium.Rectangle.fromDegrees(2.2, 3.5, 15.2, 14.5);
    this.viewer.camera.flyTo({
      destination: nigeriaRectangle,
      orientation: {
        heading: Cesium.Math.toRadians(0.0),
        pitch: Cesium.Math.toRadians(-89.9),
        roll: 0.0,
      },
      duration,
    });
  }

  /**
   * Fly camera to specific city cluster with top-down bounding sphere
   */
  flyToCity(regionName: string): void {
    this.closeBuildingCamera();
    const cluster = this.regionsSubject.getValue().find(c => c.name.toLowerCase() === regionName.toLowerCase());
    if (!cluster || !this.viewer) return;

    // Calculate bounding sphere encompassing all towers in the cluster
    const positions: Cesium.Cartesian3[] = cluster.towers.map(t =>
      Cesium.Cartesian3.fromDegrees(t.longitude, t.latitude)
    );
    positions.push(Cesium.Cartesian3.fromDegrees(cluster.centerLon, cluster.centerLat));

    const boundingSphere = Cesium.BoundingSphere.fromPoints(positions);
    // Expand radius slightly for nice visual margin
    boundingSphere.radius = Math.max(boundingSphere.radius * 1.8, 30000);

    this.viewer.camera.flyToBoundingSphere(boundingSphere, {
      offset: new Cesium.HeadingPitchRange(
        Cesium.Math.toRadians(0.0),
        Cesium.Math.toRadians(-89.9), // Looking straight down for exact centering
        0
      ),
      duration: 1.5,
    });
  }

  /**
   * Close selected tower dialog
   */
  closeTowerDetails(): void {
    this.selectedTowerSubject.next(null);
    this.hoverTooltipSubject.next(null);
  }

  /**
   * Cleanup on destroy
   */
  destroy(): void {
    this.cleanupMouseEvents();
    if (this.viewer && this.cdrDataSource) {
      try {
        if (this.viewer.dataSources.contains(this.cdrDataSource)) {
          this.viewer.dataSources.remove(this.cdrDataSource, true);
        }
      } catch (e) { }
    }
    this.cdrDataSource = undefined;
    this.viewer = undefined;
    this.isInitialized = false;
  }
}
